import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export const MAX_COMMENT_LENGTH = 2000;   // mirrors create_video_comment's cap

// Same shape the rest of the app uses for post timestamps: short dates past a
// week rather than an ever-growing "8w" counter.
export const commentTime = (iso) => {
  const then = new Date(iso);
  const mins = Math.floor((Date.now() - then.getTime()) / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const opts = { month: 'short', day: 'numeric' };
  if (then.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return then.toLocaleDateString('en-US', opts);
};

export const commentAuthorName = (c) =>
  c.author_use_username && c.author_username ? c.author_username : (c.author_name || 'DSP');

export function useVideoComments(videoId) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchComments = useCallback(async () => {
    if (!videoId) return;
    const { data, error: err } = await supabase.rpc('get_video_comments', {
      p_video_id: String(videoId),
    });
    if (err) {
      console.error('get_video_comments:', err);
      return;
    }
    setComments(data ?? []);
  }, [videoId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setComments([]);          // clear immediately — navigating between videos
    fetchComments().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fetchComments]);

  // Returns true on success so the caller knows whether to clear its input.
  const addComment = useCallback(async (content) => {
    const text = content.trim();
    if (!text) return false;

    setError('');
    const { error: err } = await supabase.rpc('create_video_comment', {
      p_video_id: String(videoId),
      p_content: text,
    });

    if (err) {
      console.error('create_video_comment:', err);
      // The RPC raises bare tokens; map the ones a user can actually hit.
      const msg = err.message || '';
      setError(
        msg.includes('not_verified')     ? 'Verify your DSP status to comment.'
        : msg.includes('content_too_long') ? 'That comment is too long.'
        : msg.includes('content_required') ? 'Write something first.'
        : 'Could not post your comment. Try again.'
      );
      return false;
    }

    await fetchComments();
    return true;
  }, [videoId, fetchComments]);

  const removeComment = useCallback(async (commentId) => {
    // Optimistic: the row is already gone from view before the round trip.
    const previous = comments;
    setComments((prev) => prev.filter((c) => c.id !== commentId));

    const { error: err } = await supabase.rpc('delete_video_comment', {
      p_comment_id: commentId,
    });

    if (err) {
      console.error('delete_video_comment:', err);
      setComments(previous);   // put it back
      setError('Could not delete that comment.');
      return false;
    }
    return true;
  }, [comments]);

  // Author-only server-side (update_video_comment, 036). Patched in place
  // rather than refetching so the thread doesn't flash and scroll position
  // survives — the row's position never changes, only its text.
  const editComment = useCallback(async (commentId, content) => {
    const { error: err } = await supabase.rpc('update_video_comment', {
      p_comment_id: commentId,
      p_content: content,
    });

    if (err) {
      console.error('update_video_comment:', err);
      const m = err.message ?? '';
      return m.includes('not_verified') ? 'Verification required to edit.'
        : m.includes('unauthorized') ? 'You can only edit your own comments.'
        : m.includes('content_required') ? "Comment can't be empty."
        : m.includes('content_too_long') ? 'That comment is too long.'
        : 'Could not save that edit.';
    }

    setComments((prev) => prev.map((c) => (
      c.id === commentId ? { ...c, content, edited_at: new Date().toISOString() } : c
    )));
    return null;
  }, []);

  return {
    comments,
    loading,
    error,
    setError,
    addComment,
    removeComment,
    editComment,
    refresh: fetchComments,
  };
}
